export default {
    assignRegEx: /{%\s*assign\s*(\w+)\s*=\s*("[^"]*"|\'[^\']*\'|(?:[^\s,\|\'"]|"[^"]*"|\'[^\']*\')+)\s*?\s*%}$/,
    placeholderRegex: /{{(\s?.[^\s]*\s?)?}}/g,
    tagsAndPlaceholderRegex: /({%.*?%}|{{.*?}})/g,
    /**
     * This produces an array of liquid tokens, which works for includes, assigns, captures and placeholders:
     * 0 = full match,
     * 1 = entire tag/placeholder
     * (optional: 2 = tag name)
     * (optional: 3 = variable key or value (value if indices 4, 5 or 6 are undefined, key otherwise))
     * (optional: 4 = value)
     * (optional: 5 = full close tag)
     * (optional: 6 = close tag name)
     */
    tokenizerRegex: /(?:({%\s(assign|include)\s(\'\w+\'|"\w+"|\w+)(?:\s=\s|\s)(.*)%}))|(?:({%\s(\w+)\s(((?!%}).)*\s\w+|\w+)((?!%}).)*%})([\s\S]*?)({%\s(\w+)?.?%}))|({{(.*?)}})/gm,

    /**
     * Previously, named capture groups were implemented to simplify the regex mapping.
     * However, it was discovered IE doesn't support named capture groups.
     * As a result, this temporary mapping was introduced as a patch to bridge
     * the named capture groups functionality without making use of them.
     * With changes such as DEV-24953, a more appropriate strategy could be implemented
     * here.
     */
    namedCaptureGroupMap: {
        'full_match': 0,
        'self_closing': 1,
        'self_closing_name': 2,
        'self_closing_variable': 3,
        'self_closing_value': 4,
        'terminated': 5,
        'terminated_name': 6,
        'terminated_variable': 7,
        'neg_lookahead_skip': 8,
        'terminated_chaff': 9,
        'terminated_value': 10,
        'terminated_close': 11,
        'terminated_close_name': 12,
        'token': 13, // refers to a variable of the form {{ variable.name }}
        'token_name': 14 // refers to variable.name in the previous example
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    findPlaceholders(content) {
        return content.match(this.placeholderRegex);
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    findTagsAndPlaceholders(content) {
        return content.match(this.tagsAndPlaceholderRegex);
    },

    assignVariables(content, variables = {}) {
        return this.mapAssign(content, variables, (full, key, value) => {
            const quoteChar = value[0];
            let token = `{% assign ${key} = ${quoteChar}${variables[key]}${quoteChar} %}`;
            return token;
        }).join('');
    },

    captureAndAssignVariables(content, variables) {
        let response = content;
        const newline = "\n";

        response = this.mapCapture(content, variables, (parts, key) => {
            if (variables[key] != null && variables[key] != '') {
                let token = `{% capture ${key} %}${variables[key]}{% endcapture %}${newline}{% assign ${key} = ${key} %}`;
                delete variables[key];
                return [token];
            }
        }).join(newline);

        response += newline;

        Object.keys(variables).forEach(key => {
            response += `{% capture ${key} %}${variables[key]}{% endcapture %}${newline}{% assign ${key} = ${key} %}${newline}`;
        });

        return `${newline}${response}`;
    },

    assignOrCreateVariables(content, variables) {
        let response = content;

        // update existsing
        response = this.mapAssign(content, variables, (full, key, value) => {
            const quoteChar = value[0];
            let token = `{% assign ${key} = ${quoteChar}${variables[key]}${quoteChar} %}`;
            delete variables[key];
            return token;
        }).join('');

        // create new assigns
        Object.keys(variables).forEach(key => {
             response += `{% assign ${key} = '${variables[key]}' %}`;
        });

        return response;
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    mapAssign(content, variables, callback) {
        const tokens = this.tokenize(content);
        const iterate = Array.isArray(variables) ? variables : Object.keys(variables);

        return tokens.map(token => {
            if (/^\{\%/.test(token)) {
                iterate.forEach(key => {
                    const parts = this.getAssignParts(token);

                    if (parts != null && parts[1] === key) {
                        token = callback(parts[0], parts[1], parts[2]);
                    }
                });
            }
            return token;
        });
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    findAssign(content, variables, callback) {
        const tokens = this.tokenize(content);

        return tokens.forEach(token => {
            if (/^\{\%/.test(token)) {
                variables.forEach(key => {
                    const parts = this.getAssignParts(token);

                    if (parts != null && parts[1] === key) {
                        callback(parts[0], parts[1], parts[2]);
                    }
                });
            }
        });
    },

    mapCapture(content, variables, callback) {
        const tokens = this.tokenizeTagsWithValues(content);
        const iterate = Array.isArray(variables) ? variables : Object.keys(variables);

        return tokens.map(token => {
            if (token == null) {
                return;
            }

            const prefix = token[this.namedCaptureGroupMap['self_closing']]
                ? 'self_closing'
                : token[this.namedCaptureGroupMap['terminated']]
                    ? 'terminated'
                    : 'token';

            const tagName = token[this.namedCaptureGroupMap[`${prefix}_name`]] || null;
            const tagVariable = token[this.namedCaptureGroupMap[`${prefix}_variable`]] || null;
            const tagValue = token[this.namedCaptureGroupMap[`${prefix}_value`]] || null;

            if (tagName == 'capture' || tagName == 'assign') {
                iterate.forEach(key => {
                    if (token != null && tagVariable === key && tagName == 'capture') {
                        token = callback(token, tagVariable);
                        return token;
                    } else if (tagVariable === key && (tagName == 'assign' && tagValue != key)) { // Remove previous assigns for the redefined capture/assign combo so that they don't conflict
                        token = null;
                    }
                });
            }

            // This could be modified in the forEach scope, so a secondary null check is necessary
            if (token != null) {
                return token[this.namedCaptureGroupMap['full_match']];
            }
        }).filter(token => typeof token !== 'undefined');
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    getAssignParts(content) {
        return content.match(this.assignRegEx);
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    tokenize(content) {
        const tokens = content.split(/(\{\%.*?assign.*?\%\})/);

        if (tokens[0] == '') {
            tokens.shift;
        }

        return tokens;
    },

    tokenizeTagsWithValues(content) {
        let matcher;
        let tokens = [];
        let tokenArray = [];
        let iteration = 0;

        while ((matcher = this.tokenizerRegex.exec(content)) !== null) {
            if (matcher.index === this.tokenizerRegex.lastIndex) {
                this.tokenizerRegex.lastIndex++;
            }

            tokens.push(matcher); // namedCaptureGroupMap handles normalization
        }

        return tokens;
    },

    tokenizePlaceholders(content) {
        let matcher;
        let tokens = [];

        while ((matcher = this.placeholderRegex.exec(content)) !== null) {
            if (matcher.index === this.placeholderRegex.lastIndex) {
                this.placeholderRegex.lastIndex++;
            }

            tokens.push(matcher.filter(value => {
                return value != null;
            })); // normalize the index order for the optional capture groups permutations and push it to the parsed tokens array
        }

        return tokens;
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    matchInclude(content) {
        content = content || '';
        return content.match(/(\{\%.*?include.*?\%\})/m);
    },

    /**
     * Deprecated by the generalized tokenizer regex, but temporarily leaving it for fallback
     * TODO: Remove this once generalized tokenizer regex is proven out
     */
    matchIncludeName(content, name) {
        const expression = `{%\\s*include\\s*("[^"]*"|\\'${name}\\'|)\\s*?\\s*%}$`;
        const regex = new RegExp(expression, 'm');

        return content.match(regex);
    },
}